import React, { useState } from "react";
import { Param, ParamTable } from "../Paramtable";
import { EndpointHeader } from "../Endpointheader";
import { CodeBlock } from "../Codeblock";
import { MethodBadge } from "../Methodbadge";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TemplateCategory = "AUTHENTICATION" | "MARKETING" | "UTILITY";
type HeaderType = "text" | "image" | "video" | "document";
type TemplateMode = "simple" | "variable";

// ─── Header options per category ──────────────────────────────────────────────

const HEADER_OPTIONS: Record<TemplateCategory, { id: HeaderType; label: string }[]> = {
    AUTHENTICATION: [
        { id: "text", label: "Text" },
    ],
    MARKETING: [
        { id: "text", label: "Text" },
        { id: "image", label: "Image" },
        { id: "video", label: "Video" },
        { id: "document", label: "Document" },
    ],
    UTILITY: [
        { id: "text", label: "Text" },
        { id: "image", label: "Image" },
        { id: "video", label: "Video" },
        { id: "document", label: "Document" },
    ],
};

// ─── Params ───────────────────────────────────────────────────────────────────

const BASE_PARAMS: Param[] = [
    { name: "templateName", type: "string", required: true, description: "Unique name to identify the template." },
    { name: "language", type: "string", required: true, description: 'Language code, e.g. "en_US".' },
    { name: "category", type: "string", required: true, description: "MARKETING, UTILITY, or AUTHENTICATION." },
    { name: "components", type: "array", required: true, description: "Array of component objects — HEADER, BODY, FOOTER, BUTTONS." },
    { name: "components[].type", type: "string", required: true, description: "Component type: HEADER, BODY, FOOTER, or BUTTONS." },
];

const HEADER_PARAMS: Record<HeaderType, Param[]> = {
    text: [
        { name: "components[].format", type: "string", required: true, description: 'Must be "TEXT" for text header.' },
        { name: "components[].text", type: "string", required: true, description: "Plain text displayed as the header." },
    ],
    image: [
        { name: "components[].format", type: "string", required: true, description: 'Must be "IMAGE".' },
        { name: "components[].mediaHandle", type: "string", required: true, description: "Handle returned from POST /api/whatsapp/media/upload." },
    ],
    video: [
        { name: "components[].format", type: "string", required: true, description: 'Must be "VIDEO".' },
        { name: "components[].mediaHandle", type: "string", required: true, description: "Handle returned from POST /api/whatsapp/media/upload." },
    ],
    document: [
        { name: "components[].format", type: "string", required: true, description: 'Must be "DOCUMENT".' },
        { name: "components[].mediaHandle", type: "string", required: true, description: "Handle returned from POST /api/whatsapp/media/upload." },
    ],
};

const VARIABLE_PARAMS: Param[] = [
    { name: "components[].text", type: "string", required: true, description: 'Body text with variables like {{1}}, {{2}}. Each {{n}} is replaced at send time.' },
    // Removed example.body_text because your actual API does not require it in the create request
];

const BUTTON_PARAMS: Param[] = [
    { name: "buttons[].type", type: "string", required: false, description: "QUICK_REPLY, URL, or PHONE_NUMBER." },
    { name: "buttons[].text", type: "string", required: false, description: "Label text shown on the button." },
    { name: "buttons[].url", type: "string", required: false, description: "Destination URL — required when type is URL." },
    { name: "buttons[].phoneNumber", type: "string", required: false, description: "E.164 phone number — required when type is PHONE_NUMBER." },
];

// ─── Payload Builders ─────────────────────────────────────────────────────────

function buildPayload(
    category: TemplateCategory,
    headerType: HeaderType,
    mode: TemplateMode
): string {
    const isVariable = mode === "variable";

    const headerComponent =
        headerType === "text"
            ? `    {
      "type": "HEADER",
      "format": "TEXT",
      "text": "${isVariable ? "Your OTP code is {{1}}" : "Welcome to our service"}"
    }`
            : `    {
      "type": "HEADER",
      "format": "${headerType.toUpperCase()}",
      "mediaHandle": "<headerHandle_from_media_upload>"
    }`;

    const bodyText = isVariable
        ? `"Your OTP code is {{1}}. It will expire in 5 minutes. Do not share this code with anyone."`
        : `"This is your ${category.toLowerCase()} template message."`;

    return `{
  "templateName": "${category.toLowerCase()}_${headerType}_${mode}",
  "language": "en_US",
  "category": "${category}",
  "components": [
${headerComponent},
    {
      "type": "BODY",
      "text": ${bodyText}
    },
    {
      "type": "FOOTER",
      "text": "Thanks"
    }
    // Buttons are optional — remove this entire component if not needed
  ]
}`;
}

function buildCurl(
    category: TemplateCategory,
    headerType: HeaderType,
    mode: TemplateMode
): string {
    const isVariable = mode === "variable";

    const headerPart =
        headerType === "text"
            ? `{ "type": "HEADER", "format": "TEXT", "text": "${isVariable ? "Your OTP code is {{1}}" : "Welcome to our service"}" }`
            : `{ "type": "HEADER", "format": "${headerType.toUpperCase()}", "mediaHandle": "<headerHandle_from_media_upload>" }`;

    const bodyPart = isVariable
        ? `{ "type": "BODY", "text": "Your OTP code is {{1}}. It will expire in 5 minutes. Do not share this code with anyone." }`
        : `{ "type": "BODY", "text": "This is your ${category.toLowerCase()} template message." }`;

    return `curl 'https://apiwhat.empedance.com/api/v1/templates/company/create' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Content-Type: application/json' \\
  --data-raw '{
    "templateName": "${category.toLowerCase()}_${headerType}_${mode}",
    "language": "en_US",
    "category": "${category}",
    "components": [
      ${headerPart},
      ${bodyPart},
      { "type": "FOOTER", "text": "Thanks" }
    ]
  }'`;
}

// ─── Static strings ───────────────────────────────────────────────────────────

const MEDIA_UPLOAD_CURL = `curl 'https://apiwhat.empedance.com/api/whatsapp/media/upload' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'file=@/path/to/your/file'`;

const MEDIA_UPLOAD_RESPONSE = `{
  "id": 26,
  "fileName": "your-file.pdf",
  "mimeType": "application/pdf",
  "fileSize": 101829,
  "headerHandle": "4::YXBwbGljYXRpb24vcGRm:ARZ7AdYEKkAsTgk7..."
}`;

// const RESPONSE_200 = `{
//   "response": "{\\"id\\":\\"950348664283600\\",\\"status\\":\\"PENDING\\",\\"category\\":\\"MARKETING\\"}",
//   "message": "Template created successfully",
//   "success": true
// }`;

function buildResponse(category: TemplateCategory): string {
    return `{
  "response": "{\\"id\\":\\"950348664283600\\",\\"status\\":\\"PENDING\\",\\"category\\":\\"${category}\\"}",
  "message": "Template created successfully",
  "success": true
}`;
}

// ─── Toggle Pill ──────────────────────────────────────────────────────────────

function TogglePill<T extends string>({
    label,
    options,
    value,
    onChange,
}: {
    label: string;
    options: { id: T; label: string }[];
    value: T;
    onChange: (v: T) => void;
}) {
    return (
        <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {label}
            </p>
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                {options.map(({ id, label: optLabel }) => (
                    <button
                        key={id}
                        onClick={() => onChange(id)}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${value === id
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        {optLabel}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── Category meta ────────────────────────────────────────────────────────────

const CATEGORY_META: Record<
    TemplateCategory,
    { title: string; description: string; color: string; bg: string; border: string }
> = {
    AUTHENTICATION: {
        title: "Authentication Template",
        description:
            "Used for OTPs, login codes, and verification flows. WhatsApp enforces stricter guidelines — no marketing content allowed. Only text headers are supported for this category.",
        color: "text-purple-800",
        bg: "bg-purple-50",
        border: "border-purple-200",
    },
    MARKETING: {
        title: "Marketing Template",
        description:
            "Promotional messages, offers, and announcements. Supports rich headers (image/video/document) and call-to-action buttons.",
        color: "text-green-800",
        bg: "bg-green-50",
        border: "border-green-200",
    },
    UTILITY: {
        title: "Utility Template",
        description:
            "Transactional messages like order updates, delivery notifications, and appointment reminders.",
        color: "text-blue-800",
        bg: "bg-blue-50",
        border: "border-blue-200",
    },
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface AddTemplateCategorySectionProps {
    category: TemplateCategory;
}

export const AddTemplateCategorySection: React.FC<AddTemplateCategorySectionProps> = ({
    category,
}) => {
    const [headerType, setHeaderType] = useState<HeaderType>("text");
    const [mode, setMode] = useState<TemplateMode>("simple");

    const isMediaType = headerType !== "text";
    const isVariable = mode === "variable";
    const meta = CATEGORY_META[category];
    const headerOptions = HEADER_OPTIONS[category];

    // Reset to text if category switches to AUTH while a media type is selected
    React.useEffect(() => {
        if (category === "AUTHENTICATION" && headerType !== "text") {
            setHeaderType("text");
        }
    }, [category]);

    const allParams: Param[] = [
        ...BASE_PARAMS,
        ...HEADER_PARAMS[headerType],
        ...(isVariable ? VARIABLE_PARAMS : []),
        ...BUTTON_PARAMS,
    ];

    return (
        <div>
            {/* Title */}
            <div className="flex items-center gap-3 mb-2">
                <MethodBadge method="POST" />
                <h1 className="text-2xl font-bold text-gray-900">{meta.title}</h1>
            </div>

            {/* Category description callout */}
            <div className={`mt-3 p-3 rounded-xl border ${meta.bg} ${meta.border}`}>
                <p className={`text-sm ${meta.color}`}>{meta.description}</p>
            </div>

            <EndpointHeader method="POST" url="/api/v1/templates/company/create" />

            {/* Filters row */}
            <div className="mt-5 flex flex-wrap gap-6">
                <TogglePill
                    label="Header Type"
                    options={headerOptions}
                    value={headerType}
                    onChange={setHeaderType}
                />

                <TogglePill
                    label="Template Mode"
                    options={[
                        { id: "simple", label: "Simple" },
                        { id: "variable", label: "Variable {{1}}" },
                    ]}
                    value={mode}
                    onChange={setMode}
                />
            </div>

            {/* Variable mode explainer */}
            {isVariable && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                    <p className="text-sm font-semibold text-indigo-800 mb-1">
                        🔢 Variable Templates
                    </p>
                    <p className="text-xs text-indigo-700">
                        Use placeholders like <code className="bg-indigo-100 px-1 rounded">{"{{1}}"}</code>,{" "}
                        <code className="bg-indigo-100 px-1 rounded">{"{{2}}"}</code> in the BODY text (and optionally in HEADER text).
                    </p>
                    <p className="text-xs text-indigo-700 mt-2">
                        Note: The <code>example</code> field is <strong>not required</strong> in this API.
                    </p>
                </div>
            )}

            {/* Media upload callout */}
            {isMediaType && (
                <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm font-semibold text-blue-800 mb-1">
                        📎 Step 1 — Upload your {headerType} first
                    </p>
                    <p className="text-xs text-blue-700 mb-3">
                        Upload the file to get a{" "}
                        <code className="bg-blue-100 px-1 rounded">headerHandle</code>. Then use that handle in the template request below.
                    </p>
                    <CodeBlock
                        title="POST /api/whatsapp/media/upload · multipart/form-data"
                        variants={[{ label: "cURL", value: MEDIA_UPLOAD_CURL }]}
                    />
                    <CodeBlock
                        title="RESPONSE 200 · OK"
                        variants={[{ label: "JSON", value: MEDIA_UPLOAD_RESPONSE }]}
                    />
                </div>
            )}

            {/* Params */}
            <ParamTable title="Request Body" params={allParams} />

            {/* Buttons note */}
            <div className="mt-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
                <span className="font-semibold text-gray-700">Buttons are optional.</span> You
                can add up to 3 buttons per template using a mix of{" "}
                <code className="bg-gray-100 px-1 rounded">QUICK_REPLY</code>,{" "}
                <code className="bg-gray-100 px-1 rounded">URL</code>, and{" "}
                <code className="bg-gray-100 px-1 rounded">PHONE_NUMBER</code>.
                Simply omit the <code>BUTTONS</code> component if you don’t need buttons.
            </div>

            {/* Code block */}
            <CodeBlock
                title="Request Example"
                variants={[
                    { label: "JSON", value: buildPayload(category, headerType, mode) },
                    { label: "cURL", value: buildCurl(category, headerType, mode) },
                ]}
            />

            {/* <CodeBlock
                title="RESPONSE 200 · OK"
                variants={[{ label: "JSON", value: RESPONSE_200 }]}
            /> */}
            <CodeBlock
                title="RESPONSE 200 · OK"
                variants={[
                    { label: "JSON", value: buildResponse(category) }
                ]}
            />

            {/* Approval note */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                <span className="font-semibold">📋 Note:</span> Newly created templates will
                have a <code className="bg-amber-100 px-1 rounded text-xs">PENDING</code> status
                until reviewed by WhatsApp. You'll receive a webhook once the template is
                approved or rejected.
            </div>
        </div>
    );
};